import { createContext, useContext, useState, useCallback, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { providerIfModuleOn } from '../../lib/hostModules';

const CrmSubscriptionDetailPanel = lazyWithReload(
  () => import('./CrmSubscriptionDetailPanel'), 'CrmSubscriptionDetailPanel');

/**
 * URL-driven `?subscriptionId=<short>` — mở panel chi tiết GÓI THUÊ BAO ở BẤT KỲ trang nào.
 *
 * Vì sao cần: card thông báo trong chat liệt kê gói sắp hết hạn / vừa bị khóa. Bấm vào mà
 * điều hướng sang `/crm/subscriptions` là **văng khỏi khung chat**, đang đọc dở thì mất chỗ
 * (sếp Phú 04.08.2026: "bấm xem thì popup chứ ko chuyển sang trang subscription làm gì").
 * Panel `CrmSubscriptionDetailPanel` vốn chỉ sống trong trang danh sách; context này nhấc nó
 * lên cấp app để mọi nơi mở được tại chỗ.
 *
 * Nguồn sự thật là query param nên link dán sang tab/chat khác vẫn mở đúng popup.
 * Trang `/crm/subscriptions?id=…` GIỮ NGUYÊN — hai đường vào song song, không thay thế nhau.
 *
 * API: useCrmSubscriptionDrawer() → { openSubscription, closeSubscription, openSubscriptionId }
 *
 * ⚠️ Hook này KHÔNG throw khi thiếu Provider (khác các drawer CRM còn lại) — nó được gọi từ
 * BotCard, mà card chat có thể render ở host chưa mount Provider. Thiếu thì trả no-op để
 * caller tự rơi về điều hướng thường, KHÔNG làm trắng cả khung chat.
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openSubscription: noProviderOpener('subscription'),
  closeSubscription: () => {},
  version: 0,
  bumpVersion: () => {},
  openSubscriptionId: null,
};

function CrmSubscriptionDrawerProviderOn({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionId = searchParams.get('subscriptionId');
  const [version, setVersion] = useState(0);

  const openSubscription = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('subscriptionId') === short) return prev;
      const next = new URLSearchParams(prev);
      next.set('subscriptionId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeSubscription = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('subscriptionId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('subscriptionId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('subscription', openSubscription), [openSubscription]);

  return (
    <Ctx.Provider value={{ openSubscription, closeSubscription, version, bumpVersion, openSubscriptionId: subscriptionId }}>
      {children}
      {subscriptionId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeSubscription} />}>
          <CrmSubscriptionDetailPanel subscriptionId={subscriptionId} onClose={closeSubscription} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useCrmSubscriptionDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}

// Cụm CRM tắt ở host (`modules.crm = false` — Site Connect 12/10/2026) ⇒ Provider rỗng: không đăng ký popup, không
// đọc query param, không dựng drawer (nội dung drawer gọi hàng chục /crm/*). Xem lib/hostModules.
export const CrmSubscriptionDrawerProvider = providerIfModuleOn('crm', CrmSubscriptionDrawerProviderOn);
